import { isStudioDesktop } from '@repo/studio-desktop-bridge/runtime'
import { Card } from '@repo/studio-design-system-ui-card'
import { Button } from '@repo/studio-design-system-ui-button'
import { IconHolder } from '@repo/studio-components-icon-holder'
import { CheckIcon } from '@repo/studio-design-system-ui-icons/general/check'
import { LayoutRightIcon } from '@repo/studio-design-system-ui-icons/layout/layout-right'
import { Maximize01Icon } from '@repo/studio-design-system-ui-icons/layout/maximize-01'
import { PopOverContainer } from '@repo/studio-design-system-ui-pop-over-container'
import * as React from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { tw } from 'utils/classnames'
import { localStore } from 'utils/storage'

import { setAidaPresentationMode, useAidaOverlayStore } from '../../stores/overlay-store'
import type { AidaPresentationMode } from '../../stores/overlay-store'
import { AIDA_VIEW_MODE_TIP_SEEN_KEY } from '../../utils/constants/storage-keys'

type ViewTile = 'dock' | 'floating' | 'fullscreen' | 'pip'
type ShownMode = AidaPresentationMode | 'pip'

const TILES = ['dock', 'floating', 'fullscreen', 'pip'] as const satisfies readonly ViewTile[]
const ONE_ROW_MIN_PX = 440

export interface AidaPictureInPictureChoice {
  active: boolean
  choose: () => void
}

export function AidaViewModeSwitcher({
  pictureInPicture,
}: {
  pictureInPicture?: AidaPictureInPictureChoice
} = {}) {
  const { t } = useTranslation('components')
  const mode = useAidaOverlayStore(state => state.mode)
  const hidePictureInPicture = isStudioDesktop()
  const pipEnabled = !hidePictureInPicture && pictureInPicture !== undefined
  const shown: ShownMode = !hidePictureInPicture && pictureInPicture?.active ? 'pip' : mode
  const tiles = shownTiles(hidePictureInPicture)
  const anchorRef = React.useRef<HTMLSpanElement>(null)
  const oneRow = useFitsOneRow(anchorRef)
  const [scrimHost, setScrimHost] = React.useState<HTMLElement | null>(null)
  const [pickerOpen, setPickerOpen] = React.useState(false)
  const [tipSeen, setTipSeen] = React.useState(
    () => localStore.getString(AIDA_VIEW_MODE_TIP_SEEN_KEY) === '1',
  )
  const showTip = !tipSeen && !pickerOpen
  const tipId = React.useId()
  const unsupportedHintId = React.useId()

  React.useLayoutEffect(() => {
    setScrimHost(anchorRef.current?.closest('header')?.parentElement ?? null)
  }, [])

  function dismissTip() {
    localStore.setString(AIDA_VIEW_MODE_TIP_SEEN_KEY, '1')
    setTipSeen(true)
  }

  function choose(next: ViewTile) {
    if (next === 'pip' || pictureInPicture?.active) {
      pictureInPicture?.choose()
    }
    if (next !== 'pip') {
      setAidaPresentationMode(next)
    }
    setPickerOpen(false)
  }

  return (
    <span ref={anchorRef} className="relative">
      <PopOverContainer.Root open={pickerOpen} onOpenChange={setPickerOpen}>
        <PopOverContainer.Trigger>
          <Button
            iconOnly
            variant="ghost"
            size="sm"
            aria-label={t('studio.components.aida.view_mode_button', { mode: modeName(t, shown) })}
            className={
              showTip ? 'ring-2 ring-theme_semantic_action_focus-ring-outer-color' : undefined
            }
          >
            {shown === 'dock' ? <LayoutRightIcon /> : null}
            {shown === 'floating' ? <FloatingWindowIcon /> : null}
            {shown === 'fullscreen' ? <Maximize01Icon /> : null}
            {shown === 'pip' ? <PictureInPictureIcon /> : null}
          </Button>
        </PopOverContainer.Trigger>
        <PopOverContainer.Content
          align="end"
          side="bottom"
          aria-label={t('studio.components.aida.view_mode')}
          className="w-fit gap-3"
        >
          <p className="studio-typography-14 text-theme_semantic_surface_ui_foreground-color-strong">
            {t('studio.components.aida.view_mode')}
          </p>
          <div
            role="radiogroup"
            aria-label={t('studio.components.aida.view_mode')}
            className={tw('grid gap-1.5', tileColumns(oneRow, hidePictureInPicture))}
          >
            {tiles.map(tile => (
              <ViewModeTile
                key={tile}
                tile={tile}
                enabled={tile === 'pip' ? pipEnabled : true}
                selected={tile === shown}
                tabIndex={tileTabIndex(tile, shown, pipEnabled)}
                choices={enabledTiles(pipEnabled)}
                hintId={tile === 'pip' && !pipEnabled ? unsupportedHintId : undefined}
                onChoose={choose}
              />
            ))}
          </div>
          {hidePictureInPicture || pipEnabled ? null : (
            <p
              id={unsupportedHintId}
              className="max-w-52 text-center studio-typography-12 text-theme_semantic_surface_ui_foreground-color-subtle"
            >
              {t('studio.components.aida.view_mode_pip_unsupported')}
            </p>
          )}
        </PopOverContainer.Content>
      </PopOverContainer.Root>
      {showTip ? <ViewModeTip tipId={tipId} onDismiss={dismissTip} /> : null}
      {showTip && scrimHost
        ? createPortal(
            <div
              aria-hidden
              className="absolute inset-0 z-10 bg-theme_semantic_surface_scrim_content_fixed-middle"
            />,
            scrimHost,
          )
        : null}
    </span>
  )
}

function ViewModeTile({
  tile,
  enabled,
  selected,
  tabIndex,
  choices,
  hintId,
  onChoose,
}: {
  tile: ViewTile
  enabled: boolean
  selected: boolean
  tabIndex: number
  choices: readonly ViewTile[]
  hintId?: string
  onChoose: (mode: ViewTile) => void
}) {
  const { t } = useTranslation('components')
  const label = tileLabel(t, tile)
  const unsupported = t('studio.components.aida.view_mode_pip_unsupported')

  return (
    <Card
      variant="interactive"
      role="radio"
      selected={selected}
      aria-label={label}
      aria-checked={selected}
      aria-describedby={hintId}
      title={hintId ? unsupported : tile === 'pip' ? label : undefined}
      disabled={!enabled}
      tabIndex={enabled ? tabIndex : undefined}
      data-mode={tile}
      padding="tight"
      radius="sm"
      className={tw(
        'relative aspect-square w-24 shrink-0 items-center justify-center gap-1 text-center',
        !enabled && 'opacity-60',
      )}
      onClick={() => {
        if (enabled) {
          onChoose(tile)
        }
      }}
      onKeyDown={event => {
        if (!enabled) {
          return
        }
        moveTile(event, tile, choices, onChoose)
      }}
    >
      {selected ? (
        <span className="absolute top-1.5 right-1.5 flex size-4 items-center justify-center rounded-full bg-theme_semantic_control_selection_selected_background_color-default text-theme_semantic_control_selection_selected_foreground_color-selected">
          <IconHolder size="xsmall">
            <CheckIcon />
          </IconHolder>
        </span>
      ) : null}
      {tile === 'dock' ? (
        <IconHolder size="large">
          <LayoutRightIcon />
        </IconHolder>
      ) : null}
      {tile === 'floating' ? (
        <IconHolder size="large">
          <FloatingWindowIcon />
        </IconHolder>
      ) : null}
      {tile === 'fullscreen' ? (
        <IconHolder size="large">
          <Maximize01Icon />
        </IconHolder>
      ) : null}
      {tile === 'pip' ? (
        <IconHolder size="large">
          <PictureInPictureIcon />
        </IconHolder>
      ) : null}
      <span className="w-full min-w-0 studio-typography-12 text-balance text-theme_semantic_surface_ui_foreground-color-strong">
        {tile === 'pip' ? t('studio.components.aida.view_mode_pip_short') : label}
      </span>
    </Card>
  )
}

function ViewModeTip({ tipId, onDismiss }: { tipId: string; onDismiss: () => void }) {
  const { t } = useTranslation('components')
  const tipRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    tipRef.current?.focus()
  }, [])

  return (
    <div
      ref={tipRef}
      role="note"
      tabIndex={-1}
      aria-labelledby={tipId}
      onKeyDown={event => {
        if (event.key !== 'Escape') {
          return
        }
        event.stopPropagation()
        event.preventDefault()
        onDismiss()
      }}
      className={tw(
        'absolute top-full right-0 z-30 mt-2 flex w-64 flex-col rounded-studio-geometry-semantic-radius-xl border p-3',
        'border-theme_semantic_surface_border_color-subtle bg-theme_semantic_surface_pop-over_primary-background shadow-studio-shadow-l4',
      )}
    >
      <span
        aria-hidden
        className="absolute -top-1.5 right-3 size-3 rotate-45 border-t border-l border-theme_semantic_surface_border_color-subtle bg-theme_semantic_surface_pop-over_primary-background"
      />
      <p
        id={tipId}
        className="studio-typography-14 text-theme_semantic_surface_ui_foreground-color-strong"
      >
        {t('studio.components.aida.view_mode_tip')}
      </p>
      <Button size="sm" variant="primary" className="mt-3 self-end" onClick={onDismiss}>
        {t('studio.components.aida.view_mode_got_it')}
      </Button>
    </div>
  )
}

function useFitsOneRow(anchorRef: React.RefObject<HTMLElement | null>) {
  const [fits, setFits] = React.useState(false)

  React.useLayoutEffect(() => {
    const panel = anchorRef.current?.closest('header')?.parentElement
    if (!panel || typeof ResizeObserver === 'undefined') {
      return
    }
    const measure = () => {
      setFits(panel.getBoundingClientRect().width >= ONE_ROW_MIN_PX)
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(panel)
    return () => observer.disconnect()
  }, [anchorRef])

  return fits
}

function shownTiles(hidePictureInPicture: boolean): readonly ViewTile[] {
  if (hidePictureInPicture) {
    return TILES.filter(tile => tile !== 'pip')
  }
  return TILES
}

function tileColumns(oneRow: boolean, hidePictureInPicture: boolean) {
  if (!oneRow) {
    return 'grid-cols-2'
  }
  if (hidePictureInPicture) {
    return 'grid-cols-3'
  }
  return 'grid-cols-4'
}

function enabledTiles(pipEnabled: boolean): readonly ViewTile[] {
  return pipEnabled ? TILES : TILES.filter(tile => tile !== 'pip')
}

function moveTile(
  event: React.KeyboardEvent<HTMLButtonElement>,
  current: ViewTile,
  choices: readonly ViewTile[],
  onChoose: (mode: ViewTile) => void,
) {
  if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') {
    return
  }
  const index = choices.indexOf(current)
  const delta = event.key === 'ArrowRight' ? 1 : -1
  const next = choices[(index + delta + choices.length) % choices.length]
  event.preventDefault()
  onChoose(next)
  event.currentTarget.parentElement
    ?.querySelector<HTMLButtonElement>(`[data-mode="${next}"]`)
    ?.focus()
}

function tileTabIndex(tile: ViewTile, shown: ShownMode, pipEnabled: boolean) {
  if (tile === 'pip' && !pipEnabled) {
    return -1
  }
  return tile === shown ? 0 : -1
}

function modeName(t: ReturnType<typeof useTranslation>['t'], mode: ShownMode) {
  switch (mode) {
    case 'fullscreen': {
      return t('studio.components.aida.presentation_fullscreen')
    }
    case 'dock': {
      return t('studio.components.aida.presentation_dock')
    }
    case 'floating': {
      return t('studio.components.aida.view_mode_floating')
    }
    case 'pip': {
      return t('studio.components.aida.view_mode_pip')
    }
  }
}

function tileLabel(t: ReturnType<typeof useTranslation>['t'], tile: ViewTile) {
  return modeName(t, tile)
}

function FloatingWindowIcon(props: React.ComponentProps<'svg'>) {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden {...props}>
      <rect x="7" y="8" width="13" height="11" rx="2" fill="currentColor" opacity="0.35" />
      <rect
        x="3.75"
        y="4.75"
        width="13"
        height="11"
        rx="2"
        stroke="currentColor"
        strokeWidth="1.5"
      />
    </svg>
  )
}

function PictureInPictureIcon(props: React.ComponentProps<'svg'>) {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden {...props}>
      <rect
        x="3.75"
        y="4.75"
        width="16.5"
        height="14.5"
        rx="2"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <rect x="12" y="12" width="7" height="5.5" rx="1" fill="currentColor" />
    </svg>
  )
}
